import React from "react"
import { createFileRoute, useLocation } from "@tanstack/react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { GradientMesh } from "@/components/ui/gradient-mesh"
import {
  GraduationCap,
  Brain,
  Users,
  Settings,
  BookOpen,
  TrendingUp,
  MessageCircle,
  MessageSquare,
  Sparkles,
  Zap,
  Target,
  CheckCircle,
  UserCheck,
  Layers,
} from "lucide-react"
import { SEO } from "@/components/SEO"
import { StructuredData } from "@/components/StructuredData"
import { trainingPage, siteMetadata } from "@/content"

export const Route = createFileRoute("/training")({
  component: TrainingPage,
})

const iconMap = {
  graduationCap: GraduationCap,
  brain: Brain,
  users: Users,
  settings: Settings,
  bookOpen: BookOpen,
  trendingUp: TrendingUp,
  messageCircle: MessageCircle,
  messageSquare: MessageSquare,
  sparkles: Sparkles,
  zap: Zap,
  target: Target,
  checkCircle: CheckCircle,
  userCheck: UserCheck,
  layers: Layers,
}

function TrainingPage() {
  const location = useLocation()
  const OverviewIcon = iconMap[trainingPage.overview.icon as keyof typeof iconMap]
  const PlatformsIcon = iconMap[trainingPage.platforms.icon as keyof typeof iconMap]
  const ProgramsIcon = iconMap[trainingPage.programs.icon as keyof typeof iconMap]
  const FormatsIcon = iconMap[trainingPage.formats.icon as keyof typeof iconMap]
  const CurriculumIcon = iconMap[trainingPage.curriculum.icon as keyof typeof iconMap]
  const OutcomesIcon = iconMap[trainingPage.outcomes.icon as keyof typeof iconMap]

  return (
    <>
      <SEO metadata={trainingPage.seo} />
      <StructuredData
        type="service"
        pageTitle="AI Training & Education"
        pageUrl={`${siteMetadata.url}${location.pathname}`}
      />
      <div className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{trainingPage.header.title}</h1>
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto">{trainingPage.header.subtitle}</p>
          </div>

          {/* Professional Hero Section */}
          <div className="relative mb-16 overflow-hidden rounded-3xl shadow-2xl">
            <GradientMesh variant="accent" size="xl" animated={true} className="h-80 md:h-96 lg:h-[28rem]" />

            {/* Floating Elements */}
            <div className="absolute inset-0">
              <div className="absolute top-1/4 left-1/4 animate-float-slow">
                <div className="p-4 glass rounded-2xl">
                  <Brain className="h-8 w-8 text-primary/70" />
                </div>
              </div>
              <div className="absolute top-1/3 right-1/4 animate-float-medium">
                <div className="p-4 glass rounded-2xl">
                  <Users className="h-8 w-8 text-purple-500/70" />
                </div>
              </div>
              <div className="absolute bottom-1/3 left-1/3 animate-float-fast">
                <div className="p-4 glass rounded-2xl">
                  <Sparkles className="h-8 w-8 text-emerald-500/70" />
                </div>
              </div>
            </div>

            {/* Central Icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="p-8 glass rounded-full animate-glow">
                <GraduationCap className="h-20 w-20 text-primary" />
              </div>
            </div>

            {/* Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-background/90 via-background/60 to-transparent">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 text-glow">
                Accelerate AI Adoption Across Your Organization
              </h2>
              <p className="text-muted-foreground text-xl">
                Hands-on training that builds real competency in AI tools and methodologies
              </p>
            </div>
          </div>

          {/* Overview */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <OverviewIcon className="h-6 w-6 text-primary" />
                {trainingPage.overview.title}
              </h2>
              <div className="space-y-4 text-muted-foreground">
                {trainingPage.overview.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Platforms */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-semibold mb-4 flex items-center justify-center gap-3">
                <PlatformsIcon className="h-8 w-8 text-primary" />
                {trainingPage.platforms.title}
              </h2>
              <p className="text-muted-foreground text-lg">{trainingPage.platforms.subtitle}</p>
            </div>

            {/* Main Program */}
            <div className="mb-8">
              <Card className="h-full">
                <CardContent className="p-8">
                  <div className="flex items-start gap-3 mb-6">
                    <div className="p-3 glass rounded-2xl shadow-lg">
                      {iconMap[trainingPage.platforms.mainProgram.icon as keyof typeof iconMap] &&
                        React.createElement(iconMap[trainingPage.platforms.mainProgram.icon as keyof typeof iconMap], {
                          className: "h-8 w-8 text-primary",
                        })}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">{trainingPage.platforms.mainProgram.name}</h3>
                      <p className="text-muted-foreground mb-4 text-lg">
                        {trainingPage.platforms.mainProgram.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {trainingPage.platforms.mainProgram.platforms.map((platform, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium"
                          >
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg">Comprehensive Training Coverage:</h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {trainingPage.platforms.mainProgram.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3 text-muted-foreground">
                          <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="pt-4 border-t border-border">
                      <p className="text-base font-medium">
                        <span className="text-primary">Ideal for:</span>{" "}
                        <span className="text-muted-foreground">{trainingPage.platforms.mainProgram.useCase}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Specialty Program */}
            <div>
              <Card className="h-full border-dashed border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-3 mb-6">
                    <div className="p-3 glass rounded-2xl shadow-lg">
                      {iconMap[trainingPage.platforms.specialtyProgram.icon as keyof typeof iconMap] &&
                        React.createElement(
                          iconMap[trainingPage.platforms.specialtyProgram.icon as keyof typeof iconMap],
                          {
                            className: "h-8 w-8 text-primary",
                          },
                        )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">{trainingPage.platforms.specialtyProgram.name}</h3>
                      <span className="inline-flex items-center px-3 py-1 rounded-lg bg-orange-100 text-orange-800 text-sm font-medium mb-3">
                        Optional Add-On
                      </span>
                      <p className="text-muted-foreground mb-4 text-lg">
                        {trainingPage.platforms.specialtyProgram.description}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg">Advanced Tools & Applications:</h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {trainingPage.platforms.specialtyProgram.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3 text-muted-foreground">
                          <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="pt-4 border-t border-border">
                      <p className="text-base font-medium">
                        <span className="text-primary">Perfect for:</span>{" "}
                        <span className="text-muted-foreground">{trainingPage.platforms.specialtyProgram.useCase}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Training Programs */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold mb-8 flex items-center gap-3">
              <ProgramsIcon className="h-6 w-6 text-primary" />
              {trainingPage.programs.title}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {trainingPage.programs.programs.map((program, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">{program.title}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <UserCheck className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{program.audience}</span>
                    </div>
                    <p className="text-muted-foreground mb-4">{program.description}</p>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Training Topics:</h4>
                      <ul className="space-y-1">
                        {program.topics.map((topic, topicIndex) => (
                          <li key={topicIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Training Formats */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <FormatsIcon className="h-6 w-6 text-primary" />
                  {trainingPage.formats.title}
                </h2>
                <div className="space-y-6">
                  {trainingPage.formats.options.map((format, index) => (
                    <div key={index}>
                      <h3 className="font-medium text-lg mb-2">{format.type}</h3>
                      <p className="text-muted-foreground mb-3">{format.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {format.benefits.map((benefit, benefitIndex) => (
                          <span
                            key={benefitIndex}
                            className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium"
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Curriculum */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <CurriculumIcon className="h-6 w-6 text-primary" />
                  {trainingPage.curriculum.title}
                </h2>
                <div className="space-y-4">
                  {trainingPage.curriculum.topics.map((topic, index) => (
                    <div key={index}>
                      <h3 className="font-medium text-lg mb-2">{topic.category}</h3>
                      <ul className="space-y-1">
                        {topic.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Training Outcomes */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <OutcomesIcon className="h-6 w-6 text-primary" />
                {trainingPage.outcomes.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {trainingPage.outcomes.results.map((result, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{result}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">{trainingPage.cta.title}</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">{trainingPage.cta.subtitle}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {trainingPage.cta.buttons.map((button, index) => (
                <Button key={index} asChild variant={button.primary ? "default" : "outline"} size="lg">
                  <a href={button.href}>{button.text}</a>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
